// src/pages/Cart.jsx
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguagesContext";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.cart-pg { background:#121212; color:#fff; font-family:'Inter',sans-serif; min-height:100vh; }

/* ── HEADER ── */
.cart-header {
  background:#0d0d0d; border-bottom:1px solid rgba(255,255,255,0.06);
  padding:88px 80px 32px;
}
.cart-header-inner { max-width:1100px; margin:0 auto; }
.cart-title { font-size:clamp(24px,4vw,40px); font-weight:900; letter-spacing:-1px; color:#fff; margin-bottom:4px; }
.cart-subtitle { font-size:14px; color:#6b6b6b; }

/* ── LAYOUT ── */
.cart-layout {
  max-width:1100px; margin:0 auto;
  padding:32px 80px 80px;
  display:grid; grid-template-columns:1fr 340px; gap:32px; align-items:start;
}

/* ── EMPTY STATE ── */
.cart-empty {
  grid-column:1/-1;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:80px 20px; text-align:center; gap:16px;
}
.cart-empty-icon { font-size:64px; }
.cart-empty-title { font-size:22px; font-weight:700; color:#fff; }
.cart-empty-sub { font-size:14px; color:#6b6b6b; max-width:280px; line-height:1.6; }
.cart-empty-btn {
  display:inline-flex; align-items:center; gap:8px;
  font-family:'Inter',sans-serif; font-size:14px; font-weight:700;
  color:#fff; background:#0078F2; border:none; border-radius:8px;
  padding:12px 28px; text-decoration:none; cursor:pointer;
  transition:background 0.15s; margin-top:8px;
}
.cart-empty-btn:hover { background:#0a84ff; }

/* ── ITEMS LIST ── */
.cart-items { display:flex; flex-direction:column; gap:12px; }

.cart-item {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.07);
  border-radius:12px; padding:16px;
  display:flex; align-items:center; gap:16px;
  transition:border-color 0.15s;
}
.cart-item:hover { border-color:rgba(255,255,255,0.12); }

.cart-item-img {
  width:72px; height:72px; border-radius:8px;
  object-fit:cover; background:#2a2a2a; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; font-size:28px;
}
.cart-item-img img { width:100%; height:100%; object-fit:cover; border-radius:8px; }

.cart-item-info { flex:1; min-width:0; }
.cart-item-genre {
  font-size:11px; font-weight:600; color:#0078F2;
  text-transform:uppercase; letter-spacing:1px; margin-bottom:4px;
}
.cart-item-title {
  font-size:16px; font-weight:700; color:#fff;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:6px;
}
.cart-item-platform {
  display:inline-flex; align-items:center; gap:5px;
  background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08);
  border-radius:4px; padding:3px 8px;
  font-size:11px; color:#6b6b6b;
}

.cart-item-right { display:flex; flex-direction:column; align-items:flex-end; gap:10px; flex-shrink:0; }
.cart-item-price { font-size:20px; font-weight:800; color:#0078F2; }
.cart-item-remove {
  font-family:'Inter',sans-serif; font-size:12px; font-weight:600;
  color:#555; background:none; border:none; cursor:pointer;
  padding:4px 8px; border-radius:4px; transition:all 0.15s;
  display:flex; align-items:center; gap:4px;
}
.cart-item-remove:hover { color:#ef4444; background:rgba(239,68,68,0.08); }

/* ── ORDER SUMMARY ── */
.cart-summary {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.07);
  border-radius:12px; padding:24px; position:sticky; top:84px;
}
.cart-summary-title { font-size:16px; font-weight:700; color:#fff; margin-bottom:20px; }

.summary-row {
  display:flex; justify-content:space-between; align-items:center;
  font-size:14px; color:#6b6b6b; margin-bottom:12px;
}
.summary-row.total {
  font-size:18px; font-weight:800; color:#fff;
  padding-top:16px; margin-top:4px;
  border-top:1px solid rgba(255,255,255,0.08);
  margin-bottom:20px;
}
.summary-row.total span:last-child { color:#0078F2; }

.cart-checkout-btn {
  display:block; width:100%; text-align:center;
  font-family:'Inter',sans-serif; font-size:15px; font-weight:700;
  color:#fff; background:#0078F2; border:none; border-radius:8px;
  padding:14px; cursor:pointer; text-decoration:none; transition:all 0.15s;
  margin-bottom:10px;
}
.cart-checkout-btn:hover { background:#0a84ff; transform:translateY(-1px); }
.cart-checkout-btn:disabled {
  background:#2a2a2a; color:#555; cursor:not-allowed; transform:none;
}

.cart-continue-btn {
  display:block; width:100%; text-align:center;
  font-family:'Inter',sans-serif; font-size:14px; font-weight:600;
  color:#a3a3a3; background:rgba(255,255,255,0.04);
  border:1px solid rgba(255,255,255,0.08); border-radius:8px;
  padding:12px; cursor:pointer; text-decoration:none; transition:all 0.15s;
}
.cart-continue-btn:hover { color:#fff; background:rgba(255,255,255,0.08); }

.summary-note {
  display:flex; align-items:flex-start; gap:8px;
  margin-top:16px; padding:12px; border-radius:8px;
  background:rgba(0,120,242,0.06); border:1px solid rgba(0,120,242,0.12);
  font-size:12px; color:#a3c4f8; line-height:1.55;
}

/* ── RESPONSIVE ── */
@media(max-width:900px) {
  .cart-header { padding:80px 24px 28px; }
  .cart-layout { grid-template-columns:1fr; padding:24px 24px 60px; gap:20px; }
  .cart-summary { position:static; }
}

@media(max-width:767px) {
  .cart-header { padding:72px 16px 24px; }
  .cart-layout { padding:16px 16px 80px; gap:16px; }

  .cart-item { padding:12px; gap:12px; }
  .cart-item-img { width:60px; height:60px; font-size:22px; }
  .cart-item-title { font-size:14px; }
  .cart-item-price { font-size:17px; }

  .cart-summary { border-radius:12px; }
}
`;

// Trash icon
const IconTrash = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

export default function Cart() {
  const { items, removeFromCart, clearCart, cartCount, cartTotal } = useCart();
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  // Each item is purchased individually (one game account = one order).
  // Checkout navigates to the first item; remaining items stay in cart
  // so the user can checkout them after completing payment.
  function handleCheckout() {
    if (!user) { navigate("/login"); return; }
    navigate(`/checkout/${items[0].id}`);
  }

  return (
    <>
      <style>{S}</style>
      <div className="cart-pg">
        <Navbar />

        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-inner">
            <div className="cart-title">🛒 My Cart</div>
            <div className="cart-subtitle">
              {cartCount === 0
                ? "Your cart is empty"
                : `${cartCount} item${cartCount > 1 ? "s" : ""} in your cart`}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="cart-layout">
          {cartCount === 0 ? (
            <div className="cart-empty">
              <div className="cart-empty-icon">🎮</div>
              <div className="cart-empty-title">Your cart is empty</div>
              <div className="cart-empty-sub">
                Browse our store and add games you want to buy
              </div>
              <Link to="/store" className="cart-empty-btn">
                Browse Store →
              </Link>
            </div>
          ) : (
            <>
              {/* Items */}
              <div>
                <div className="cart-items">
                  {items.map(game => (
                    <div key={game.id} className="cart-item">
                      {/* Cover */}
                      <div className="cart-item-img">
                        {game.coverImage
                          ? <img src={game.coverImage} alt={game.title} />
                          : "🎮"
                        }
                      </div>

                      {/* Info */}
                      <div className="cart-item-info">
                        {game.genre && <div className="cart-item-genre">{game.genre}</div>}
                        <div className="cart-item-title">{game.title}</div>
                        {game.platform && (
                          <div className="cart-item-platform">🖥 {game.platform}</div>
                        )}
                      </div>

                      {/* Price + remove */}
                      <div className="cart-item-right">
                        <div className="cart-item-price">${game.price?.toFixed(2)}</div>
                        <button
                          className="cart-item-remove"
                          onClick={() => removeFromCart(game.id)}
                        >
                          <IconTrash /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Clear all */}
                {cartCount > 1 && (
                  <button
                    onClick={clearCart}
                    style={{
                      marginTop:16, background:"none", border:"none",
                      color:"#555", fontFamily:"'Inter',sans-serif",
                      fontSize:13, fontWeight:600, cursor:"pointer",
                      display:"flex", alignItems:"center", gap:6,
                      padding:"6px 0", transition:"color 0.15s"
                    }}
                    onMouseEnter={e => e.currentTarget.style.color="#ef4444"}
                    onMouseLeave={e => e.currentTarget.style.color="#555"}
                  >
                    <IconTrash /> Clear all items
                  </button>
                )}
              </div>

              {/* Summary */}
              <div className="cart-summary">
                <div className="cart-summary-title">Order Summary</div>

                {items.map(g => (
                  <div key={g.id} className="summary-row">
                    <span style={{ maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {g.title}
                    </span>
                    <span>${g.price?.toFixed(2)}</span>
                  </div>
                ))}

                <div className="summary-row total">
                  <span>Total</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>

                <button
                  className="cart-checkout-btn"
                  onClick={handleCheckout}
                >
                  {!user
                    ? "Login to Checkout"
                    : items.length > 1
                      ? `Checkout ${items[0].title?.slice(0, 20) || "first item"} →`
                      : "Proceed to Checkout"}
                </button>
                {items.length > 1 && user && (
                  <div style={{ fontSize:11, color:"#6b6b6b", textAlign:"center", marginBottom:8 }}>
                    Items are purchased one at a time. Complete each payment to continue.
                  </div>
                )}

                <Link to="/store" className="cart-continue-btn">
                  Continue Shopping
                </Link>

                <div className="summary-note">
                  💳 Payment via ABA Pay QR — secure & instant delivery after confirmation.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}